import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Space, 
  Avatar, 
  Typography, 
  Badge, 
  Button, 
  Input, 
  theme, 
  Tag, 
  message,
  Spin,
  Modal,
  Tooltip,
  Card,
  Alert
} from 'antd';
import { 
  UserOutlined, 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  ReloadOutlined,
  BugOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { 
  getAllPatients, 
  deletePatient,
  getPatientById
} from '../api/addpatientApi';

const { Title, Text } = Typography;
const { useToken } = theme;
const { confirm } = Modal;

const PatientTable = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const { token } = useToken();
  const navigate = useNavigate();

  // Debug function to test NocoDB connection
  const runDebugTests = async () => {
    console.log('=== STARTING NOCODB DEBUG ===');
    setDebugMode(true);
    
    const debugResults = {
      connectionTest: null,
      fetchTest: null,
      samplePatient: null
    };
    
    try {
      // Test 1: Try to fetch patients
      console.log('1. Testing patient fetch...');
      try {
        const result = await getAllPatients(5); // Get just 5 for testing
        debugResults.fetchTest = { 
          success: true, 
          count: result?.list?.length || result?.length || 0,
          structure: result 
        };
        
        // Test 2: Get sample patient details if any exist
        if (result?.list?.length > 0) {
          console.log('2. Testing single patient fetch...');
          const firstPatient = result.list[0];
          const patientId = firstPatient.recId || firstPatient.id;
          if (patientId) {
            try {
              debugResults.samplePatient = await getPatientById(patientId);
            } catch (error) {
              debugResults.samplePatient = { error: error.message };
            }
          }
        }
        
      } catch (error) {
        debugResults.fetchTest = { error: error.message };
      }
      
      debugResults.connectionTest = { success: true, message: 'API functions loaded' };
      
    } catch (error) {
      console.error('Debug tests failed:', error);
      debugResults.connectionTest = { error: error.message };
    }
    
    console.log('=== DEBUG RESULTS ===', debugResults);
    setDebugInfo(debugResults);
    return debugResults;
  };

  // Fetch patients from NocoDB
  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching patients...');
      
      // Use your actual API function
      const result = await getAllPatients(100);
      console.log('Raw result from getAllPatients:', result);
      
      // Handle the response structure from your API
      let patientsData = [];
      
      if (result && Array.isArray(result.list)) {
        patientsData = result.list;
      } else if (Array.isArray(result)) {
        patientsData = result;
      } else {
        console.warn('Unexpected result structure:', result);
        patientsData = [];
      }
      
      console.log('Processed patients data:', patientsData);
      
      // Transform the data to match table structure
      const transformedPatients = patientsData.map((patient, index) => {
        const patientId = patient.recId || patient.id || index;
        
        return {
          key: patientId,
          id: patientId,
          name: `${patient.firstName || ''} ${patient.middleName || ''} ${patient.lastName || ''}`.trim() || 'Unknown',
          firstName: patient.firstName || '',
          lastName: patient.lastName || '',
          age: patient.birthDate ? calculateAge(patient.birthDate) : 'N/A',
          gender: patient.gender || 'Not specified',
          mobile: patient.mobileNumber ? patient.mobileNumber.toString() : '',
          email: patient.email || '',
          address: formatAddress(patient),
          maritalStatus: patient.maritalStatus || 'Not specified',
          photo: patient.photo,
          createdAt: patient.createdAt || patient.created_at || new Date().toISOString(),
          status: 'Active',
          lastVisit: formatDate(patient.createdAt || patient.created_at) || 'No visits',
          avatarColor: getAvatarColor(patient.firstName || 'U')
        };
      });

      console.log('Transformed patients:', transformedPatients);
      
      setPatients(transformedPatients);
      setFilteredPatients(transformedPatients);
      
      if (transformedPatients.length === 0) {
        message.info('No patients found. Try running debug tests to check your NocoDB connection.');
      } else {
        message.success(`Loaded ${transformedPatients.length} patients successfully`);
      }
      
    } catch (error) {
      console.error('Error fetching patients:', error);
      message.error(`Failed to load patients: ${error.message}`);
      
      // If fetch fails, suggest running debug
      message.info('Consider running debug tests to diagnose the issue');
    } finally {
      setLoading(false);
    }
  }, []);

  // Calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Format address using your API field names
  const formatAddress = (patient) => {
    const parts = [
      patient.addLineone,
      patient.addLinetwo,
      patient.addCity,
      patient.addState,
      patient.addPostalcode
    ].filter(Boolean);
    return parts.join(', ') || 'No address';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [
      '#f56a00', '#7265e6', '#ffbf00', '#00a2ae', 
      '#f56565', '#9f7aea', '#4299e1', '#48bb78',
      '#ed8936', '#38b2ac', '#667eea', '#f093fb'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(patient =>
        patient.name.toLowerCase().includes(value.toLowerCase()) ||
        patient.mobile.includes(value) ||
        patient.email.toLowerCase().includes(value.toLowerCase()) ||
        patient.id.toString().toLowerCase().includes(value.toLowerCase())
      );
      setFilteredPatients(filtered);
    }
  };

  // Handle delete patient
  const handleDelete = (patientId) => {
    confirm({
      title: 'Delete Patient',
      content: 'Are you sure you want to delete this patient? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          console.log('Deleting patient with ID:', patientId);
          await deletePatient(patientId);
          message.success('Patient deleted successfully');
          fetchPatients(); // Refresh the list
        } catch (error) {
          console.error('Error deleting patient:', error);
          message.error(`Failed to delete patient: ${error.message}`);
        }
      }
    });
  };

  // Load patients on component mount
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const patientColumns = [
    {
      title: 'Patient',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 250,
      render: (text, record) => (
        <Space>
          <Avatar 
            size="large"
            src={record.photo?.[0]?.url}
            style={{ 
              background: record.photo?.[0]?.url ? 'transparent' : `linear-gradient(135deg, ${record.avatarColor} 0%, ${token.colorPrimaryHover} 100%)`,
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {!record.photo?.[0]?.url && (record.firstName?.charAt(0) || 'U')}
          </Avatar>
          <div>
            <Text strong style={{ display: 'block', color: token.colorTextHeading }}>
              {text || 'Unknown Patient'}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ID: {record.id}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.gender} • {record.age} years
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.mobile && (
            <Space size="small">
              <PhoneOutlined style={{ color: token.colorTextSecondary, fontSize: '12px' }} />
              <Text style={{ fontSize: '13px' }}>{record.mobile}</Text>
            </Space>
          )}
          {record.email && (
            <Space size="small">
              <MailOutlined style={{ color: token.colorTextSecondary, fontSize: '12px' }} />
              <Text style={{ fontSize: '13px' }}>{record.email}</Text>
            </Space>
          )}
          {!record.mobile && !record.email && (
            <Text type="secondary" style={{ fontSize: '13px' }}>No contact info</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 200,
      render: (address) => (
        <Tooltip title={address}>
          <Text 
            style={{ 
              fontSize: '13px',
              display: 'block',
              maxWidth: '180px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {address.length > 30 ? `${address.substring(0, 30)}...` : address}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Badge 
          color={status === 'Active' ? 'green' : 'gray'} 
          text={
            <Text style={{ 
              color: status === 'Active' ? token.green6 : token.colorTextSecondary,
              fontWeight: 500,
              fontSize: '13px'
            }}>
              {status}
            </Text>
          } 
        />
      )
    },
    {
      title: 'Marital Status',
      dataIndex: 'maritalStatus',
      key: 'maritalStatus',
      width: 130,
      render: (text) => (
        <Tag 
          color="blue" 
          style={{ 
            borderRadius: '12px',
            padding: '0 8px',
            background: token.blue1,
            borderColor: token.blue3,
            color: token.blue7,
            fontSize: '12px'
          }}
        >
          {text}
        </Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => (
        <Space size="small">
          <CalendarOutlined style={{ color: token.colorTextSecondary, fontSize: '12px' }} />
          <Text style={{ fontSize: '13px' }}>{formatDate(date)}</Text>
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="text" 
              shape="circle" 
              icon={<UserOutlined />}
              size="small"
              onClick={() => {
                message.info(`View details for ${record.name}`);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit Patient">
            <Button 
              type="text" 
              shape="circle" 
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                navigate(`/patient/edit/${record.key}`);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete Patient">
            <Button 
              type="text" 
              shape="circle" 
              icon={<DeleteOutlined />}
              size="small"
              danger
              onClick={() => handleDelete(record.key)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', background: token.colorBgLayout }}>
      {/* Debug Section */}
      {debugMode && (
        <Card style={{ marginBottom: '16px' }} size="small">
          <Alert
            message="Debug Mode Active"
            description="Check the browser console for detailed debug information."
            type="info"
            showIcon
            action={
              <Button 
                size="small" 
                onClick={() => setDebugMode(false)}
              >
                Hide Debug
              </Button>
            }
          />
          {debugInfo && (
            <div style={{ marginTop: '8px', fontSize: '12px' }}>
              <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          )}
        </Card>
      )}

      {/* Main Content */}
      <Card>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <Title level={5} style={{ 
            color: token.colorTextHeading, 
            margin: 0,
            fontWeight: 700,
            fontSize: '16px'
          }}>
            Patient Records ({filteredPatients.length})
          </Title>
          <Space>
            <Input 
              placeholder="Search patients..." 
              prefix={<SearchOutlined style={{ color: token.colorTextSecondary }} />}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ 
                width: 250,
                borderRadius: '12px',
                background: token.colorBgElevated,
                borderColor: token.colorBorder
              }}
              allowClear
            />
            
            <Button 
              icon={<BugOutlined />}
              onClick={runDebugTests}
              style={{ borderRadius: '12px' }}
            >
              Debug
            </Button>
            
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchPatients}
              style={{ borderRadius: '12px' }}
            >
              Refresh
            </Button>
            
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              style={{ 
                borderRadius: '12px',
                fontWeight: 500
              }}
              onClick={() => navigate('/patient/add-patient')}
            >
              Add Patient
            </Button>
          </Space>
        </div>

        <Spin spinning={loading} tip="Loading patients...">
          <Table 
            columns={patientColumns}
            dataSource={filteredPatients}
            rowKey="key"
            scroll={{ x: 1200 }}
            pagination={{
              position: ['bottomRight'],
              showSizeChanger: true,
              pageSizeOptions: ['4', '8'],
              showTotal: (total, range) => 
                `Showing ${range[0]}-${range[1]} of ${total} patients`,
              showQuickJumper: true,
              defaultPageSize: 4
            }}
            locale={{
              emptyText: patients.length === 0 ? 
                'No patients found. Click "Debug" to test your NocoDB connection.' : 
                'No patients match your search criteria.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default PatientTable;